import React from "react";
import { eur, fmtDate } from "../lib/api";

// Document = estimate or invoice. type='estimate'|'invoice'
export default function DocumentPreview({ doc, company, type = "estimate", logoUrl }) {
  if (!doc) return null;
  const tpl = doc.template_id || company?.template_id || "classic";
  const items = doc.items || [];
  const isISP = !!doc.isp;
  const isRect = doc.invoice_type === "rectificativa";
  const isOrigin = !!doc.is_origin;
  const irpfPct = doc.irpf_pct || 0;
  const lineNet = (i, qty) => (qty || 0) * (i.unit_price || 0) * (1 - (i.discount_pct || 0) / 100);
  const monthlyBase = items.reduce((s, i) => s + lineNet(i, i.quantity), 0);
  const previousCertified = isOrigin ? items.reduce((s, i) => s + lineNet(i, i.previous_quantity), 0) : 0;
  const totalCertified = monthlyBase + previousCertified;
  const contractTotal = doc.contract_total || 0;
  const contractRemaining = contractTotal - totalCertified;
  const contractPct = contractTotal > 0 ? Math.min(100, (totalCertified / contractTotal) * 100) : 0;
  const subtotal = monthlyBase;
  const tax = isISP ? 0 : items.reduce((s, i) => s + lineNet(i, i.quantity) * (i.tax_pct || 0) / 100, 0);
  const irpfAmount = monthlyBase * irpfPct / 100;
  const total = monthlyBase + tax - irpfAmount;
  const title = type === "invoice" ? (isRect ? "FACTURA RECTIFICATIVA" : "FACTURA") : "PRESUPUESTO";

  const Common = ({ children, accent = "#0A1128", style = {} }) => (
    <div className="doc-print bg-white text-[#0A1128] p-10 mx-auto shadow-2xl" style={{ width: "210mm", minHeight: "297mm", ...style }} data-testid="document-preview">
      {children}
    </div>
  );

  const Header = ({ accent }) => (
    <div className="flex justify-between items-start mb-8">
      <div>
        {logoUrl ? <img src={logoUrl} alt="logo" className="h-24 mb-3 object-contain max-w-[280px]" /> : <div className="font-display font-bold text-2xl">{company?.name || "Mi Empresa"}</div>}
        <div className="text-xs mt-1 text-[#374151]">
          {company?.cif && <div>CIF: {company.cif}</div>}
          {company?.address && <div>{company.address}</div>}
          {company?.phone && <div>Tel: {company.phone}</div>}
          {company?.email && <div>{company.email}</div>}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display font-black text-3xl tracking-tight" style={{ color: accent }}>{title}</div>
        <div className="font-mono text-sm mt-1">Nº {doc.number}</div>
        <div className="text-xs mt-2">{fmtDate(doc.issue_date || doc.created_at)}</div>
        {type === "estimate" && doc.valid_until && <div className="text-xs">Válido hasta: {fmtDate(doc.valid_until)}</div>}
        {type === "invoice" && doc.due_date && <div className="text-xs">Vencimiento: {fmtDate(doc.due_date)}</div>}
        {doc.reference && <div className="text-xs mt-1">Ref.: <strong>{doc.reference}</strong></div>}
        {doc.project && <div className="text-xs">Obra: <strong>{doc.project}</strong></div>}
      </div>
    </div>
  );

  const ClientBox = ({ accent }) => (
    <div className="mb-6 p-4" style={{ background: "#F8FAFC", borderLeft: `4px solid ${accent}` }}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-1">Cliente</div>
      <div className="font-semibold">{doc.client_snapshot?.name || "—"}</div>
      <div className="text-xs text-[#374151]">
        {doc.client_snapshot?.cif && <div>CIF/NIF: {doc.client_snapshot.cif}</div>}
        {doc.client_snapshot?.address && <div>{doc.client_snapshot.address}</div>}
        {doc.client_snapshot?.email && <div>{doc.client_snapshot.email}</div>}
      </div>
    </div>
  );

  const ItemsTable = ({ accent, dense }) => {
    if (isOrigin) {
      return (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr style={{ background: accent, color: "#fff" }}>
              <th className="text-left p-2 font-medium">Descripción</th>
              <th className="text-right p-2 font-medium">Med. actual</th>
              <th className="text-right p-2 font-medium">Med. anterior</th>
              <th className="text-right p-2 font-medium">A origen</th>
              <th className="text-right p-2 font-medium">Precio</th>
              <th className="text-right p-2 font-medium">Total línea</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const qNow = it.quantity || 0;
              const qPrev = it.previous_quantity || 0;
              const medOrigen = qNow + qPrev;
              const lineTotal = medOrigen * it.unit_price * (1 - (it.discount_pct || 0) / 100);
              return (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                  <td className={`p-2 ${dense ? "" : "py-3"}`}>{it.description}</td>
                  <td className="p-2 text-right">{qNow}</td>
                  <td className="p-2 text-right">{qPrev}</td>
                  <td className="p-2 text-right font-medium">{medOrigen}</td>
                  <td className="p-2 text-right">{eur(it.unit_price)}</td>
                  <td className="p-2 text-right font-semibold">{eur(lineTotal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }
    return (
      <table className="w-full text-sm mb-4">
        <thead>
          <tr style={{ background: accent, color: "#fff" }}>
            <th className="text-left p-2 font-medium">Descripción</th>
            <th className="text-right p-2 font-medium w-16">Cant.</th>
            <th className="text-right p-2 font-medium w-16">Ud.</th>
            <th className="text-right p-2 font-medium w-24">Precio</th>
            <th className="text-right p-2 font-medium w-16">IVA</th>
            <th className="text-right p-2 font-medium w-24">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const line = it.quantity * it.unit_price * (1 - (it.discount_pct || 0) / 100);
            return (
              <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                <td className={`p-2 ${dense ? "" : "py-3"}`}>{it.description}</td>
                <td className="p-2 text-right">{it.quantity}</td>
                <td className="p-2 text-right">{it.unit}</td>
                <td className="p-2 text-right">{eur(it.unit_price)}</td>
                <td className="p-2 text-right">{it.tax_pct}%</td>
                <td className="p-2 text-right font-semibold">{eur(line)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const RectBox = () => {
    if (!isRect) return null;
    return (
      <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 text-amber-900">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-1">Factura rectificativa (art. 13 RD 1619/2012)</div>
        <div className="text-sm">Rectifica la factura <strong>{doc.rectifies_invoice_number}</strong></div>
        {doc.rectifies_reason && <div className="text-xs italic mt-1">Motivo: {doc.rectifies_reason}</div>}
      </div>
    );
  };

  const Totals = ({ accent }) => (
    <div className="mb-4">
      <div className="flex justify-end">
        <div className="w-80">
          {isOrigin ? <>
            <div className="flex justify-between py-1 text-sm"><span>Total certificado a origen</span><span>{eur(totalCertified)}</span></div>
            <div className="flex justify-between py-1 text-sm text-[#6B7280]"><span>Certificaciones anteriores</span><span>-{eur(previousCertified)}</span></div>
            <div className="flex justify-between py-1 text-sm pt-2 border-t border-[#E5E7EB]"><span>Total en el mes (base imp.)</span><span>{eur(monthlyBase)}</span></div>
          </> : <div className="flex justify-between py-1 text-sm"><span>Base imponible</span><span>{eur(subtotal)}</span></div>}
          {!isISP && <div className="flex justify-between py-1 text-sm"><span>IVA</span><span>{eur(tax)}</span></div>}
          {irpfPct > 0 && <div className="flex justify-between py-1 text-sm text-[#B91C1C]"><span>IRPF ({irpfPct}%)</span><span>-{eur(irpfAmount)}</span></div>}
          <div className="flex justify-between py-2 mt-1 font-display font-bold text-lg border-t-2" style={{ borderColor: accent }}>
            <span>{isOrigin ? "A FACTURAR" : "TOTAL"}</span><span style={{ color: accent }}>{eur(total)}</span>
          </div>
          {isOrigin && contractTotal > 0 && (
            <div className="mt-3 pt-2 border-t border-[#E5E7EB] text-xs space-y-1" data-testid="contract-progress-preview">
              <div className="flex justify-between"><span className="text-[#374151]">Total contrato</span><span className="font-semibold">{eur(contractTotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#374151]">Facturado a la fecha</span><span>{eur(totalCertified)}</span></div>
              <div className={`flex justify-between font-semibold ${contractRemaining < 0 ? "text-[#B91C1C]" : "text-[#047857]"}`}>
                <span>{contractRemaining < 0 ? "Excedido" : "Pendiente"}</span>
                <span>{eur(Math.abs(contractRemaining))}</span>
              </div>
              <div className="h-1.5 bg-[#E5E7EB] rounded overflow-hidden mt-1">
                <div className="h-full" style={{ width: `${contractPct}%`, background: contractRemaining < 0 ? "#B91C1C" : contractPct > 90 ? "#D97706" : "#047857" }} />
              </div>
              <div className="text-[10px] text-[#6B7280] text-right">{contractPct.toFixed(1)}% del contrato</div>
            </div>
          )}
        </div>
      </div>
      {isISP && (
        <div className="mt-3 p-2 text-xs italic border-l-4 border-amber-500 bg-amber-50 text-amber-900">
          Operación con inversión del sujeto pasivo conforme al art. 84 LIVA. El IVA será autoliquidado por el destinatario.
        </div>
      )}
    </div>
  );

  const PaymentBox = ({ accent }) => {
    if (type !== "invoice" || (!doc.payment_method && !doc.bank_account)) return null;
    return (
      <div className="mb-4 p-3" style={{ background: "#EEF2FF", borderLeft: `4px solid ${accent}` }}>
        <div className="text-[10px] uppercase tracking-widest font-bold mb-1">Forma de pago</div>
        {doc.payment_method && <div className="text-sm">{doc.payment_method}</div>}
        {doc.bank_account && <div className="text-xs font-mono mt-1">IBAN: <strong>{doc.bank_account}</strong></div>}
      </div>
    );
  };

  const Footer = () => (
    <div className="text-xs text-[#6B7280] border-t pt-3 mt-4">
      {doc.notes && <div className="mb-2 whitespace-pre-line">{doc.notes}</div>}
      <div>Documento generado con Vel7o.com</div>
    </div>
  );

  const accent = doc.template_id ? (require("../lib/templates").TEMPLATES.find(t => t.id === doc.template_id)?.accent || "#0A1128") : "#0A1128";

  switch (tpl) {
    case "modern":
      return <Common accent={accent}>
        <div className="grid grid-cols-[110px_1fr] gap-6 mb-8">
          <div className="bg-[#00F0FF] flex items-end p-3" style={{ minHeight: 140 }}>
            <div className="text-white font-display font-black text-xs uppercase tracking-[0.3em] [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">{title}</div>
          </div>
          <div>
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-20 object-contain max-w-[280px] mb-2" /> : <div className="font-display font-black text-3xl">{company?.name}</div>}
            <div className="text-xs text-[#374151] leading-snug">
              {company?.cif && <div>CIF: {company.cif}</div>}
              {company?.address && <div>{company.address}</div>}
              {company?.phone && <div>Tel: {company.phone}</div>}
            </div>
            <div className="mt-4 pt-3 border-t border-[#E5E7EB] flex justify-between text-xs">
              <div><span className="text-[#6B7280] uppercase tracking-widest">Nº</span> <strong className="font-mono">{doc.number}</strong></div>
              <div><span className="text-[#6B7280] uppercase tracking-widest">Fecha</span> {fmtDate(doc.issue_date || doc.created_at)}</div>
              {doc.reference && <div><span className="text-[#6B7280] uppercase tracking-widest">Ref</span> {doc.reference}</div>}
            </div>
          </div>
        </div>
        <div className="border-l-4 border-[#00F0FF] pl-4 mb-6">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#00F0FF]">Facturar a</div>
          <div className="font-semibold text-lg">{doc.client_snapshot?.name || "—"}</div>
          <div className="text-xs text-[#374151]">{doc.client_snapshot?.cif} · {doc.client_snapshot?.address}</div>
        </div>
        <RectBox /><ItemsTable accent="#00F0FF" /><Totals accent="#00F0FF" /><PaymentBox accent="#00F0FF" /><Footer />
      </Common>;

    case "minimal":
      return <Common accent="#111827" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
        <div className="flex justify-between items-start mb-12">
          <div>
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-12 object-contain max-w-[200px] grayscale mb-3" /> : <div className="font-light text-lg tracking-wide">{company?.name}</div>}
            <div className="text-[10px] text-[#6B7280] leading-relaxed mt-1">{[company?.cif, company?.address, company?.email].filter(Boolean).join(" · ")}</div>
          </div>
          <div className="text-right">
            <div className="uppercase tracking-[0.4em] text-[10px] text-[#6B7280]">{title}</div>
            <div className="font-light text-3xl mt-1">{doc.number}</div>
            <div className="text-xs text-[#6B7280] mt-2">{fmtDate(doc.issue_date || doc.created_at)}</div>
            {doc.reference && <div className="text-[10px] text-[#6B7280]">{doc.reference}</div>}
          </div>
        </div>
        <div className="mb-10 text-sm">
          <span className="text-[#9CA3AF] uppercase tracking-widest text-[10px] mr-3">Cliente</span>
          {doc.client_snapshot?.name} {doc.client_snapshot?.cif && <span className="text-[#6B7280]">· {doc.client_snapshot.cif}</span>}
        </div>
        <RectBox /><ItemsTable accent="#111827" /><Totals accent="#111827" /><PaymentBox accent="#111827" /><Footer />
      </Common>;

    case "bold":
      return <Common accent="#FF7A00" style={{ paddingTop: 0 }}>
        <div className="px-10 py-10 -mx-10 -mt-10 mb-10" style={{ background: "linear-gradient(135deg, #FF7A00 0%, #FF9500 100%)", color: "#fff" }}>
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold opacity-80 mb-2">{title}</div>
          <div className="font-display font-black text-6xl leading-none mb-4">{doc.number}</div>
          <div className="flex justify-between items-end text-sm">
            <div>{fmtDate(doc.issue_date || doc.created_at)} {doc.reference && `· Ref. ${doc.reference}`}</div>
            <div className="text-right">
              {logoUrl ? <img src={logoUrl} alt="logo" className="h-14 object-contain max-w-[180px] brightness-0 invert" /> : <div className="font-display font-bold">{company?.name}</div>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 mb-6 text-xs">
          <div><div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">DE</div><div className="font-bold text-sm">{company?.name}</div><div>{company?.cif}<br/>{company?.address}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">PARA</div><div className="font-bold text-sm">{doc.client_snapshot?.name || "—"}</div><div>{doc.client_snapshot?.cif}<br/>{doc.client_snapshot?.address}</div></div>
        </div>
        <RectBox /><ItemsTable accent="#FF7A00" /><Totals accent="#FF7A00" /><PaymentBox accent="#FF7A00" /><Footer />
      </Common>;

    case "corporate":
      return <Common accent="#0055FF" style={{ borderTop: "8px solid #0055FF", marginTop: 0, paddingTop: 28 }}>
        <div className="grid grid-cols-2 gap-8 mb-6 pb-4 border-b-2 border-[#0055FF]">
          <div>
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-16 object-contain max-w-[240px] mb-2" /> : <div className="font-display font-bold text-xl">{company?.name}</div>}
            <div className="text-[11px] text-[#374151] leading-snug">
              {company?.address}<br/>{company?.cif} · {company?.phone}<br/>{company?.email}
            </div>
          </div>
          <div className="text-right border-l-2 border-[#E5E7EB] pl-6">
            <div className="font-display font-bold text-2xl" style={{ color: "#0055FF" }}>{title}</div>
            <div className="font-mono text-sm mt-1">Nº {doc.number}</div>
            <div className="text-xs text-[#374151]">{fmtDate(doc.issue_date || doc.created_at)}</div>
            {type === "invoice" && doc.due_date && <div className="text-xs">Vto: {fmtDate(doc.due_date)}</div>}
            {doc.reference && <div className="text-xs mt-1"><strong>Ref.:</strong> {doc.reference}</div>}
          </div>
        </div>
        <table className="w-full text-xs mb-5">
          <tbody>
            <tr>
              <td className="bg-[#F1F5F9] p-2 w-32 font-bold uppercase tracking-widest text-[10px]">Facturar a</td>
              <td className="p-2 border-l border-[#E5E7EB]"><strong>{doc.client_snapshot?.name || "—"}</strong> {doc.client_snapshot?.cif && `· ${doc.client_snapshot.cif}`}<br/><span className="text-[#6B7280]">{doc.client_snapshot?.address}</span></td>
            </tr>
          </tbody>
        </table>
        <RectBox /><ItemsTable accent="#0055FF" /><Totals accent="#0055FF" /><PaymentBox accent="#0055FF" /><Footer />
      </Common>;

    case "creative":
      return <Common accent="#FF7A00" style={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}>
        <div className="grid grid-cols-[80px_1fr] min-h-full">
          <div className="text-white p-6 flex flex-col justify-between" style={{ background: "linear-gradient(180deg, #00F0FF 0%, #0055FF 50%, #FF7A00 100%)", minHeight: "270mm" }}>
            <div className="font-display font-black text-xs uppercase tracking-[0.4em] [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">{title} · {doc.number}</div>
            <div className="text-[9px] opacity-80 [writing-mode:vertical-rl] rotate-180">{fmtDate(doc.issue_date || doc.created_at)}</div>
          </div>
          <div className="p-8">
            <div className="flex justify-between items-start mb-8">
              <div>
                {logoUrl ? <img src={logoUrl} alt="logo" className="h-16 object-contain max-w-[220px] mb-2" /> : <div className="font-display font-black text-xl">{company?.name}</div>}
                <div className="text-[11px] text-[#374151]">{company?.cif} · {company?.address}</div>
              </div>
              <div className="text-right text-xs">
                <div className="text-[10px] uppercase tracking-widest text-[#FF7A00] font-bold">Para</div>
                <div className="font-bold text-sm">{doc.client_snapshot?.name || "—"}</div>
                <div className="text-[#6B7280]">{doc.client_snapshot?.cif}</div>
                {doc.reference && <div className="text-[11px] mt-2"><span className="text-[#FF7A00] font-bold uppercase tracking-widest text-[9px]">Ref</span> {doc.reference}</div>}
              </div>
            </div>
            <RectBox /><ItemsTable accent="#FF7A00" /><Totals accent="#FF7A00" /><PaymentBox accent="#FF7A00" /><Footer />
          </div>
        </div>
      </Common>;

    case "elegant":
      return <Common accent="#0A1128" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        <div className="text-center pb-6 mb-6" style={{ borderBottom: "3px double #0A1128" }}>
          {logoUrl && <img src={logoUrl} alt="logo" className="h-20 object-contain mx-auto mb-4" />}
          <div className="font-bold text-lg uppercase tracking-[0.6em]">{company?.name || "Empresa"}</div>
          <div className="text-[11px] italic text-[#6B7280] mt-1">{[company?.address, company?.cif, company?.email].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="text-center mb-8">
          <div className="uppercase tracking-[0.5em] text-xs">{title}</div>
          <div className="text-3xl font-bold italic mt-1">Nº {doc.number}</div>
          <div className="text-xs italic text-[#6B7280] mt-1">{fmtDate(doc.issue_date || doc.created_at)} {doc.reference && `— Ref. ${doc.reference}`}</div>
        </div>
        <div className="flex justify-between text-xs mb-8 px-8">
          <div className="italic"><div className="text-[10px] uppercase tracking-widest not-italic text-[#6B7280] mb-0.5">A la atención de</div>{doc.client_snapshot?.name}<br/>{doc.client_snapshot?.cif}<br/>{doc.client_snapshot?.address}</div>
        </div>
        <RectBox /><ItemsTable accent="#0A1128" /><Totals accent="#0A1128" /><PaymentBox accent="#0A1128" /><Footer />
      </Common>;

    case "compact":
      return <Common accent="#0D1838" style={{ fontSize: 11, padding: "20mm 15mm" }}>
        <div className="grid grid-cols-3 gap-3 mb-3 pb-2 border-b-[3px] border-[#0D1838]">
          <div className="col-span-2">
            <div className="flex items-center gap-3">
              {logoUrl ? <img src={logoUrl} alt="logo" className="h-12 object-contain max-w-[160px]" /> : <div className="font-display font-bold">{company?.name}</div>}
              <div className="text-[9px] leading-tight">
                <div className="font-bold">{company?.name}</div>
                <div>{company?.cif} · {company?.phone}</div>
                <div>{company?.address}</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-base" style={{ color: "#0D1838" }}>{title}</div>
            <div className="font-mono text-xs">{doc.number}</div>
            <div className="text-[9px]">{fmtDate(doc.issue_date || doc.created_at)}</div>
            {type === "invoice" && doc.due_date && <div className="text-[9px]">Vto: {fmtDate(doc.due_date)}</div>}
            {doc.reference && <div className="text-[9px]">Ref: {doc.reference}</div>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px] mb-3 p-2 border border-[#E5E7EB]">
          <div><span className="text-[#6B7280] uppercase tracking-wider text-[8px]">Cliente</span><br/><strong>{doc.client_snapshot?.name || "—"}</strong> {doc.client_snapshot?.cif && `· ${doc.client_snapshot.cif}`}</div>
          <div><span className="text-[#6B7280] uppercase tracking-wider text-[8px]">Dirección</span><br/>{doc.client_snapshot?.address || "—"}</div>
        </div>
        <RectBox /><ItemsTable accent="#0D1838" dense /><Totals accent="#0D1838" /><PaymentBox accent="#0D1838" /><Footer />
      </Common>;

    case "classic":
    default:
      return <Common accent="#0A1128">
        <div className="flex justify-between items-end pb-3 mb-5 border-b-2 border-[#0A1128]">
          <div>
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-20 object-contain max-w-[260px] mb-2" /> : <div className="font-display font-bold text-2xl">{company?.name}</div>}
            <div className="text-[11px] text-[#374151]">{company?.cif && `CIF: ${company.cif}`} {company?.address && `· ${company.address}`}</div>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-2xl tracking-wider">{title}</div>
            <div className="font-mono text-sm">Nº {doc.number}</div>
            <div className="text-xs">{fmtDate(doc.issue_date || doc.created_at)}</div>
            {type === "invoice" && doc.due_date && <div className="text-xs">Vencimiento: {fmtDate(doc.due_date)}</div>}
            {doc.reference && <div className="text-xs"><strong>Ref.:</strong> {doc.reference}</div>}
          </div>
        </div>
        <div className="mb-5 text-sm">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">Cliente:</span> <strong>{doc.client_snapshot?.name || "—"}</strong>
          <div className="text-xs text-[#374151] mt-1">{doc.client_snapshot?.cif} {doc.client_snapshot?.address && `· ${doc.client_snapshot.address}`}</div>
        </div>
        <RectBox /><ItemsTable accent="#0A1128" /><Totals accent="#0A1128" /><PaymentBox accent="#0A1128" /><Footer />
      </Common>;
  }
}
